import { z } from "zod";
import { RichText } from "../RichText";
import { ctaColorEnum, ctaClasses } from "../ctaColors";
import { globalSectionSchema, backgroundStyleEnum } from "../globalProps";
import { SectionShell } from "../SectionShell";

export const schema = globalSectionSchema.extend({
  heading: z.string().default("Ready to Book?"),
  body: z
    .string()
    .default(
      "Schedule a consultation with one of our experienced professionals. Choose a time that works for you, and we will handle the rest.",
    ),
  ctaLabel: z.string().default("Book a Consultation"),
  ctaHref: z.string().default("/book"),
  showFeatures: z.boolean().default(true),
  features: z
    .array(z.string())
    .default([
      "Choose phone or video",
      "Flexible scheduling",
      "No commitment required",
    ]),
  ctaTarget: z.enum(["_self", "_blank"]).default("_self"),
  ctaColor: ctaColorEnum.optional(),
  backgroundStyle: backgroundStyleEnum.default("primaryBg"),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

/** Backgrounds that historically produced an inverted (white) CTA button */
const darkBackgrounds = new Set(["primary", "secondary", "dark", "brand", "tertiary", "primaryBg"]);

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;

  return (
    <SectionShell globals={safe} bare>
      <div className="max-w-4xl mx-auto px-6 text-center">
        <RichText
          as={safe.headingLevel}
          html={safe.heading}
          className="text-3xl @md:text-[2.5rem] font-bold mb-4"
          data-prop="heading"
        />
        <RichText
          as="p"
          html={safe.body}
          className="text-lg @md:text-xl opacity-90 mb-8 max-w-2xl mx-auto"
          data-prop="body"
        />
        {safe.showFeatures && safe.features.length > 0 && (
          <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-8 text-sm @md:text-base opacity-85">
            {safe.features.map((feature, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="text-current opacity-70">&#10003;</span>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        )}
        {safe.ctaLabel && safe.ctaHref && (() => {
          const ctaButtonClasses = safe.ctaColor
            ? ctaClasses(safe.ctaColor)
            : darkBackgrounds.has(safe.backgroundStyle)
              ? "bg-white text-primary hover:opacity-90"
              : "bg-primary text-white hover:opacity-90";
          return (
            <a
              href={safe.ctaHref}
              target={safe.ctaTarget}
              rel={safe.ctaTarget === "_blank" ? "noopener noreferrer" : undefined}
              className={`inline-block px-10 py-4 text-lg font-semibold rounded-lg transition-opacity ${ctaButtonClasses}`}
            >
              <RichText html={safe.ctaLabel} data-prop="ctaLabel" />
            </a>
          );
        })()}
      </div>
    </SectionShell>
  );
}
